<template>
  <div
    class="asistente"
    :class="{ 'is-open': isOpen }"
  >
    <transition name="asistente-pop">
      <section
        v-if="isOpen"
        class="asistente-panel"
        role="dialog"
        aria-modal="false"
        aria-labelledby="asistente-title"
        @keydown.esc="close"
      >
        <header class="asistente-header">
          <div class="asistente-avatar">
            <i class="zmdi zmdi-comment-text" />
          </div>
          <div class="asistente-heading">
            <h2 id="asistente-title">
              Asistente UNC
            </h2>
            <p>Respuestas sobre tu Usuario UNC</p>
          </div>
          <button
            class="asistente-icon-btn"
            title="Nueva conversación"
            aria-label="Nueva conversación"
            :disabled="isSending || messages.length === 0"
            @click="resetConversation"
          >
            <svg
              viewBox="0 0 24 24"
              width="19"
              height="19"
              fill="none"
              stroke="currentColor"
              stroke-width="1.8"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
            >
              <path d="M20 12.5c0 4.14-3.58 7.5-8 7.5-1.2 0-2.34-.25-3.36-.7L4 20.5l1.2-3.6A7.15 7.15 0 0 1 4 12.5C4 8.36 7.58 5 12 5s8 3.36 8 7.5Z" />
              <path d="M12 9.5v6M9 12.5h6" />
            </svg>
          </button>
          <button
            class="asistente-icon-btn"
            title="Cerrar"
            aria-label="Cerrar asistente"
            @click="close"
          >
            <i class="zmdi zmdi-close" />
          </button>
        </header>

        <div
          ref="thread"
          class="asistente-thread"
          aria-live="polite"
        >
          <div class="asistente-msg is-bot">
            <div class="asistente-bubble">
              ¡Hola! Soy el asistente de Usuarios UNC. Contame tu duda y te ayudo a resolverla.
            </div>
          </div>

          <div
            v-if="messages.length === 0"
            class="asistente-suggestions"
          >
            <button
              v-for="suggestion in suggestions"
              :key="suggestion"
              class="asistente-chip"
              @click="send(suggestion)"
            >
              {{ suggestion }}
            </button>
          </div>

          <div
            v-for="(message, index) in renderedMessages"
            :key="index"
            class="asistente-msg"
            :class="message.who === 'usuario' ? 'is-user' : 'is-bot'"
          >
            <div
              class="asistente-bubble"
              :class="{ 'is-notice': isNotice(message), 'is-error': message.error }"
            >
              <template v-for="(block, blockIndex) in message.blocks">
                <ul
                  v-if="block.type === 'list'"
                  :key="blockIndex"
                  class="asistente-list"
                >
                  <li
                    v-for="(item, itemIndex) in block.items"
                    :key="itemIndex"
                    :class="{ 'is-nested': item.nested }"
                  >
                    <span class="asistente-marker">{{ item.marker }}</span>
                    <highlighted-text :parts="item.parts" />
                  </li>
                </ul>
                <p
                  v-else
                  :key="blockIndex"
                >
                  <highlighted-text :parts="block.parts" />
                </p>
              </template>
            </div>

            <div
              v-if="message.options && message.options.length && index === messages.length - 1"
              class="asistente-suggestions"
            >
              <button
                v-for="option in message.options"
                :key="option.label"
                class="asistente-chip"
                :disabled="isSending"
                @click="send(option.sends)"
              >
                {{ option.label }}
              </button>
            </div>
          </div>

          <div
            v-if="isSending"
            class="asistente-msg is-bot"
          >
            <span class="sr-only">El asistente está escribiendo</span>
            <div
              class="asistente-bubble asistente-typing"
              aria-hidden="true"
            >
              <span /><span /><span />
            </div>
          </div>
        </div>

        <form
          class="asistente-composer"
          @submit.prevent="send(draft)"
        >
          <textarea
            ref="input"
            v-model="draft"
            rows="1"
            :maxlength="maxLength"
            placeholder="Escribí tu consulta…"
            aria-label="Tu consulta"
            @keydown.enter.exact.prevent="send(draft)"
            @input="autoGrow"
          />
          <button
            type="submit"
            class="asistente-send"
            aria-label="Enviar"
            :disabled="isSending || !draft.trim()"
          >
            <i class="zmdi zmdi-mail-send" />
          </button>
        </form>
        <p class="asistente-disclaimer">
          Las respuestas se generan automáticamente usando IA y pueden contener errores.
        </p>
      </section>
    </transition>

    <transition name="asistente-fade">
      <div
        v-if="showHint && !isOpen"
        class="asistente-hint"
      >
        <button
          class="asistente-hint-body"
          @click="open"
        >
          <strong>¿Necesitás ayuda?</strong>
          <span>Preguntale al asistente sobre tu Usuario UNC.</span>
        </button>
        <button
          class="asistente-hint-close"
          aria-label="Ocultar mensaje"
          @click="dismissHint"
        >
          <i class="zmdi zmdi-close" />
        </button>
      </div>
    </transition>

    <button
      ref="launcher"
      class="asistente-launcher"
      :aria-label="isOpen ? 'Cerrar asistente' : 'Abrir asistente'"
      :aria-expanded="String(isOpen)"
      @click="isOpen ? close() : open()"
    >
      <i
        class="zmdi"
        :class="isOpen ? 'zmdi-close' : 'zmdi-comment-text'"
      />
    </button>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { HttpFRONT } from '@/share';
import { parseBoldSegments } from '@/utils/richText';
import HighlightedText from '../utils/highlighted-text.vue';

const STORAGE_KEY = 'asistente-unc';
const HINT_DELAY_MS = 1500;
const NOTICE_OUTCOMES = ['abstained', 'out_of_scope'];
const LIST_ITEM_REGEX = /^\s*((?:\d+|[a-z])[.)]|[-*•])\s+(.*)$/i;
const HEADING_REGEX = /^\s*#{1,6}\s+(.*)$/;

function readStorage () {
    try {
        return JSON.parse(window.sessionStorage.getItem(STORAGE_KEY)) || {};
    } catch (e) {
        return {};
    }
}

// No hace falta que sea impredecible: el BFF sólo lo usa para separar las conversaciones de una misma sesión.
function newConversationId () {
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function formatText (text) {
    const blocks = [];

    (text || '').split('\n').forEach(rawLine => {
        const line = rawLine.trimEnd();
        const last = blocks[blocks.length - 1];

        if (!line.trim()) {
            blocks.push({ type: 'break' });
            return;
        }

        const item = line.match(LIST_ITEM_REGEX);
        if (item) {
            const isBullet = /^[-*•]$/.test(item[1]);
            const entry = {
                marker: isBullet ? '•' : item[1],
                nested: isBullet && Boolean(last && last.type === 'list' && last.items.some(i => i.marker !== '•')),
                parts: parseBoldSegments(item[2], { links: true }),
            };
            if (last && last.type === 'list') {
                last.items.push(entry);
            } else {
                blocks.push({ type: 'list', items: [entry] });
            }
            return;
        }

        const heading = line.match(HEADING_REGEX);
        const content = heading ? `**${heading[1]}**` : line;

        if (last && last.type === 'paragraph') {
            last.parts.push({ text: '\n', isBold: false }, ...parseBoldSegments(content, { links: true }));
        } else {
            blocks.push({ type: 'paragraph', parts: parseBoldSegments(content, { links: true }) });
        }
    });

    return blocks.filter(block => block.type !== 'break');
}

function writeStorage (value) {
    try {
        window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(value));
    } catch (e) {
        // Sin sessionStorage la conversación solo dura hasta recargar.
    }
}

export default {
    name: 'AsistenteWidget',
    components: {
        HighlightedText,
    },
    data () {
        const stored = readStorage();
        return {
            isOpen: false,
            showHint: false,
            hintDismissed: Boolean(stored.hintDismissed),
            isSending: false,
            draft: '',
            // El thread vive en la sesión del BFF; acá sólo se recuerda qué conversación es y si el próximo mensaje abre una nueva.
            conversationId: stored.conversationId || newConversationId(),
            startFresh: stored.startFresh !== false,
            messages: stored.messages || [],
            hintTimeoutId: null,
            suggestions: [
                '¿Cómo creo mi Usuario UNC?',
                'Olvidé mi contraseña',
                '¿Para qué sirve el correo de recuperación?',
            ],
        };
    },
    computed: {
        ...mapGetters(['configApp']),
        maxLength () {
            return this.configApp.asistenteMaxQueryLength;
        },
        // En un computed y no en el template: si no, cada tecla del textarea re-parsea todos los mensajes.
        renderedMessages () {
            return this.messages.map(message => ({ ...message, blocks: formatText(message.text) }));
        },
    },
    mounted () {
        if (!this.hintDismissed) {
            this.hintTimeoutId = window.setTimeout(() => {
                this.showHint = true;
            }, HINT_DELAY_MS);
        }
    },
    beforeDestroy () {
        window.clearTimeout(this.hintTimeoutId);
    },
    methods: {
        open () {
            this.isOpen = true;
            this.dismissHint();
            this.$nextTick(() => {
                this.scrollToBottom();
                if (this.$refs.input) this.$refs.input.focus();
            });
        },
        close () {
            this.isOpen = false;
            this.$nextTick(() => this.$refs.launcher && this.$refs.launcher.focus());
        },
        dismissHint () {
            window.clearTimeout(this.hintTimeoutId);
            this.showHint = false;
            this.hintDismissed = true;
            this.persist();
        },
        resetConversation () {
            this.messages = [];
            this.conversationId = newConversationId();
            this.startFresh = true;
            this.persist();
            this.$nextTick(() => this.$refs.input && this.$refs.input.focus());
        },
        async send (text) {
            const query = (text || '').trim();
            if (!query || this.isSending) return;

            this.messages.push({ who: 'usuario', text: query });
            this.draft = '';
            this.isSending = true;
            this.$nextTick(() => {
                this.autoGrow();
                this.scrollToBottom();
            });

            try {
                const { data } = await HttpFRONT.post('asistente/query', {
                    query,
                    conversation_id: this.conversationId,
                    new_thread: this.startFresh,
                });
                // Recién acá: si la consulta falla, el reintento tiene que seguir abriendo una nueva.
                this.startFresh = false;
                if (data.context_reset) {
                    this.messages.push({
                        who: 'asistente',
                        notice: true,
                        text: 'Se perdió el contexto de la conversación anterior, así que esta respuesta no lo tiene en cuenta.',
                    });
                }
                this.messages.push({
                    who: 'asistente',
                    text: data.text,
                    outcome: data.outcome,
                    options: data.options || [],
                });
            } catch (error) {
                const message = error.response && error.response.data && error.response.data.message;
                this.messages.push({
                    who: 'asistente',
                    error: true,
                    text: message || 'No pudimos contactar al asistente. Probá de nuevo en unos minutos.',
                });
            } finally {
                this.isSending = false;
                this.persist();
                this.$nextTick(this.scrollToBottom);
            }
        },
        persist () {
            writeStorage({
                hintDismissed: this.hintDismissed,
                conversationId: this.conversationId,
                startFresh: this.startFresh,
                messages: this.messages,
            });
        },
        scrollToBottom () {
            const thread = this.$refs.thread;
            if (thread) thread.scrollTop = thread.scrollHeight;
        },
        autoGrow () {
            const input = this.$refs.input;
            if (!input) return;
            input.style.height = 'auto';
            input.style.height = `${Math.min(input.scrollHeight, 120)}px`;
        },
        isNotice (message) {
            return message.notice || NOTICE_OUTCOMES.includes(message.outcome);
        },
    },
};
</script>

<style scoped>
.asistente {
  --asistente-ease: cubic-bezier(0.16, 1, 0.3, 1);
  position: fixed;
  right: 24px;
  bottom: 24px;
  z-index: 1040;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 14px;
  font-family: var(--font-body);
}

.asistente button {
  font-family: var(--font-ui);
}

/* Lanzador */

.asistente-launcher {
  position: relative;
  width: 60px;
  height: 60px;
  border: none;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--unc-azul), var(--unc-azul-mid));
  color: white;
  font-size: 26px;
  cursor: pointer;
  box-shadow: 0 10px 28px rgba(0, 56, 101, 0.35);
  transition: transform 0.3s var(--asistente-ease), box-shadow 0.3s var(--asistente-ease);
}

.asistente-launcher::after {
  content: '';
  position: absolute;
  inset: -16px;
  border-radius: 50%;
  border: 2px solid var(--unc-verde-light);
  opacity: 0;
  pointer-events: none;
  will-change: transform, opacity;
  backface-visibility: hidden;
  animation:
    asistente-ring-scale 2.8s cubic-bezier(0.25, 0.6, 0.4, 1) 2s infinite,
    asistente-ring-fade 2.8s linear 2s infinite;
}

.asistente.is-open .asistente-launcher::after {
  animation: none;
}

.asistente-launcher:hover {
  transform: translateY(-3px);
  box-shadow: 0 14px 34px rgba(0, 56, 101, 0.42);
}

.asistente-launcher:focus-visible {
  outline: 3px solid var(--unc-verde-light);
  outline-offset: 3px;
}

@keyframes asistente-ring-scale {
  0% { transform: scale(0.7); }
  50%, 100% { transform: scale(1); }
}

@keyframes asistente-ring-fade {
  0% { opacity: 0; }
  6% { opacity: 0.75; }
  42%, 100% { opacity: 0; }
}

/* Burbuja de ayuda */

.asistente-hint {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: flex-start;
  max-width: 260px;
  margin-right: 6px;
  background: var(--card-bg);
  border: 1px solid var(--border);
  border-radius: 16px;
  box-shadow: 0 12px 32px rgba(0, 56, 101, 0.14);
}

.asistente-hint::after {
  content: '';
  position: absolute;
  right: 22px;
  bottom: -7px;
  width: 14px;
  height: 14px;
  background: var(--card-bg);
  border-right: 1px solid var(--border);
  border-bottom: 1px solid var(--border);
  transform: rotate(45deg);
}

.asistente-hint-body {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 12px 4px 12px 16px;
  border: none;
  background: transparent;
  text-align: left;
  cursor: pointer;
}

.asistente-hint-body strong {
  font-size: 14px;
  font-weight: 600;
  color: var(--unc-azul);
}

.asistente-hint-body span {
  font-size: 13px;
  color: var(--text-muted);
  line-height: 1.4;
}

.asistente-hint-close {
  flex-shrink: 0;
  margin: 6px 6px 0 0;
  width: 26px;
  height: 26px;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: var(--text-muted);
  cursor: pointer;
  transition: background 0.2s ease;
}

.asistente-hint-close:hover {
  background: rgba(0, 56, 101, 0.06);
}

/* Panel */

.asistente-panel {
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  width: 380px;
  height: min(600px, calc(100vh - 128px));
  /* La franja oscura evita que el fondo claro asome en las esquinas del header. */
  background: linear-gradient(#001e3c 0 24px, var(--bg) 24px);
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 24px 60px rgba(0, 30, 60, 0.25);
  transform-origin: bottom right;
}

.asistente-header {
  position: relative;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 12px 16px 18px;
  background: #001e3c;
  color: white;
}

.asistente-header::after {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 3px;
  background: linear-gradient(90deg, var(--unc-azul-light), var(--unc-verde-light));
}

.asistente-avatar {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(62, 182, 184, 0.18);
  color: var(--unc-verde-light);
  font-size: 20px;
}

.asistente-heading {
  flex: 1;
  min-width: 0;
}

.asistente-heading h2 {
  margin: 0;
  font-family: var(--font-display);
  font-size: 19px;
  font-weight: 700;
  color: white;
}

.asistente-heading p {
  margin: 2px 0 0;
  font-size: 12px;
  letter-spacing: 0.3px;
  color: rgba(255, 255, 255, 0.65);
}

.asistente-icon-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: rgba(255, 255, 255, 0.75);
  font-size: 18px;
  cursor: pointer;
  transition: background 0.25s ease, color 0.25s ease;
}

.asistente-icon-btn:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.1);
  color: white;
}

.asistente-icon-btn:disabled {
  opacity: 0.35;
  cursor: default;
}

.asistente-thread {
  flex: 1;
  overflow-y: auto;
  padding: 18px 16px 8px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  overscroll-behavior: contain;
}

.asistente-msg {
  display: flex;
  flex-direction: column;
  gap: 8px;
  animation: asistente-rise 0.35s var(--asistente-ease);
}

.asistente-msg.is-user {
  align-items: flex-end;
}

.asistente-msg.is-bot {
  align-items: flex-start;
}

@keyframes asistente-rise {
  from { opacity: 0; transform: translateY(6px); }
  to { opacity: 1; transform: none; }
}

.asistente-bubble {
  max-width: 88%;
  padding: 10px 14px;
  font-size: 14px;
  line-height: 1.5;
  overflow-wrap: anywhere;
  border-radius: 16px;
}

.is-bot .asistente-bubble {
  background: var(--card-bg);
  color: var(--text);
  border: 1px solid var(--border);
  border-bottom-left-radius: 4px;
  box-shadow: 0 4px 14px rgba(0, 56, 101, 0.05);
}

.is-user .asistente-bubble {
  background: linear-gradient(135deg, var(--unc-azul), var(--unc-azul-mid));
  color: white;
  border-bottom-right-radius: 4px;
}

.asistente-bubble.is-notice {
  border-left: 3px solid var(--unc-verde-light);
}

.asistente-bubble.is-error {
  border-color: rgba(192, 57, 43, 0.25);
  background: #fdf3f2;
  color: #8a2b20;
}

.asistente-bubble p {
  margin: 0;
  white-space: pre-line;
}

.asistente-bubble p + p,
.asistente-bubble p + .asistente-list,
.asistente-bubble .asistente-list + p {
  margin-top: 8px;
}

.asistente-bubble strong {
  font-weight: 600;
  color: var(--unc-azul);
}

.is-user .asistente-bubble strong {
  color: white;
}

.asistente-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.asistente-list li {
  display: flex;
  gap: 6px;
}

.asistente-list li.is-nested {
  padding-left: 22px;
}

.asistente-marker {
  flex-shrink: 0;
  min-width: 16px;
  font-weight: 600;
  color: var(--unc-azul-light);
}

.asistente-suggestions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.asistente-chip {
  padding: 7px 14px;
  font-size: 13px;
  font-weight: 500;
  color: var(--unc-azul);
  background: var(--card-bg);
  border: 1px solid rgba(0, 56, 101, 0.18);
  border-radius: 100px;
  cursor: pointer;
  text-align: left;
  transition: all 0.25s var(--asistente-ease);
}

.asistente-chip:hover:not(:disabled) {
  border-color: var(--unc-verde-light);
  background: rgba(62, 182, 184, 0.08);
  transform: translateY(-1px);
}

.asistente-chip:disabled {
  opacity: 0.5;
  cursor: default;
}

.asistente-typing {
  display: flex;
  gap: 4px;
  padding: 14px 16px;
}

.asistente-typing span {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--unc-azul-light);
  opacity: 0.35;
  animation: asistente-dot 1.2s infinite ease-in-out;
}

.asistente-typing span:nth-child(2) { animation-delay: 0.15s; }
.asistente-typing span:nth-child(3) { animation-delay: 0.3s; }

@keyframes asistente-dot {
  0%, 80%, 100% { opacity: 0.35; transform: translateY(0); }
  40% { opacity: 1; transform: translateY(-3px); }
}

.asistente-composer {
  display: flex;
  align-items: flex-end;
  gap: 8px;
  margin: 8px 12px 0;
  padding: 6px 6px 6px 16px;
  background: var(--card-bg);
  border: 1px solid rgba(0, 56, 101, 0.15);
  border-radius: 24px;
  transition: border-color 0.25s ease, box-shadow 0.25s ease;
}

.asistente-composer:focus-within {
  border-color: var(--unc-verde-light);
  box-shadow: 0 0 0 3px rgba(62, 182, 184, 0.15);
}

.asistente-composer textarea {
  flex: 1;
  min-height: 34px;
  max-height: 120px;
  padding: 7px 0;
  border: none;
  outline: none;
  resize: none;
  background: transparent;
  font-family: var(--font-body);
  font-size: 14px;
  line-height: 1.45;
  color: var(--text);
}

.asistente-send {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 50%;
  background: var(--unc-azul);
  color: white;
  font-size: 17px;
  cursor: pointer;
  transition: background 0.25s ease, transform 0.25s var(--asistente-ease);
}

.asistente-send:hover:not(:disabled) {
  background: var(--unc-azul-light);
  transform: translateY(-1px);
}

.asistente-send:disabled {
  background: rgba(0, 56, 101, 0.25);
  cursor: default;
}

.asistente-disclaimer {
  margin: 6px 16px 10px;
  font-size: 11px;
  text-align: center;
  color: var(--text-muted);
}

/* Transiciones */

.asistente-pop-enter-active,
.asistente-pop-leave-active {
  transition: opacity 0.25s ease, transform 0.35s var(--asistente-ease);
}

.asistente-pop-enter,
.asistente-pop-leave-to {
  opacity: 0;
  transform: translateY(12px) scale(0.96);
}

.asistente-fade-enter-active,
.asistente-fade-leave-active {
  transition: opacity 0.3s ease, transform 0.4s var(--asistente-ease);
}

.asistente-fade-leave-active {
  position: absolute;
  right: 0;
  bottom: 74px;
}

.asistente-fade-enter,
.asistente-fade-leave-to {
  opacity: 0;
  transform: translateY(8px);
}

@media (prefers-reduced-motion: reduce) {
  .asistente *,
  .asistente *::after {
    animation: none !important;
    transition: none !important;
  }
}

@media (max-width: 480px) {
  .asistente {
    right: 16px;
    bottom: 16px;
  }

  .asistente-panel {
    position: fixed;
    inset: 0;
    width: 100%;
    height: 100%;
    border-radius: 0;
  }

  .asistente.is-open .asistente-launcher {
    display: none;
  }
}
</style>
